import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import ServiceManager from 'services/ServiceManager';
import { memberIdSelector } from '../redux-store/auth/authSelectors';
import { useFormatting } from '../locale';

import { DEFAULT_PAGE_SIZE } from '../pages/ClientOverview/pages/Dashboard/components/OverviewApplications/constants/const';

interface InvestmentApplication {
    investmentApplicationId: number | string;
    [key: string]: unknown;
}

interface ServiceResponse<T> {
    data: {
        investmentApplicationInfos: T[];
        totalCount: number;
    };
}

interface AdapterParams<T> {
    data: T[];
    t: (key: string, options?: unknown) => string;
    getFormattedDate: (...args: unknown[]) => string;
}

type Adapter<T, R> = (params: AdapterParams<T>) => R;

interface UseGetInvestmentApplicationsReturn<R> {
    isLoading: boolean;
    error: Error | null;
    data: R | null;
    totalPages: number;
}

export const useGetInvestmentApplications = <
    T extends InvestmentApplication,
    R = unknown,
>({
        pageNumber,
        adapter,
    }: {
    pageNumber: number;
    adapter: Adapter<T, R>;
}): UseGetInvestmentApplicationsReturn<R> => {
    const memberId = useSelector(memberIdSelector);

    const {
        t,
        i18n: { language },
    } = useTranslation();

    const { getFormattedDate } = useFormatting();

    const {
        data: response,
        isLoading,
        error,
    } = useQuery<ServiceResponse<T>, Error>({
        queryKey: [
            'getInvestmentApplications',
            { pageNumber, language, memberId },
        ],
        queryFn: () => ServiceManager.customInvestmentService(
            'getInvestmentApplications',
            [
                {
                    pageNumber,
                    language,
                    pageSize: DEFAULT_PAGE_SIZE,
                    memberId,
                },
            ],
        ) as Promise<ServiceResponse<T>>,
        enabled: !!memberId,
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes
    });

    const rawData = response?.data?.investmentApplicationInfos ?? null;
    const totalPages = response?.data?.totalCount ?? 0;

    const tableData = useMemo(() => {
        if (!rawData) return null;

        return adapter({
            data: rawData,
            t,
            getFormattedDate,
        });
    }, [rawData, t, getFormattedDate, adapter]);

    return {
        isLoading,
        error: error ?? null,
        data: tableData,
        totalPages,
    };
};
